import { memo, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import './incidents.css';
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import IncidentsTable from '../../components/Incidents/IncidentsTable';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  getDepartmentsList,
  getFormSettings,
  getIncidentInfo,
  getIncidentsList,
  syncNeris,
} from '../../store/incidentsReducer';
import useIncidentsColumns from './hooks/useIncidentsColumns';
import ViewIncidentModal from '../../components/Incidents/modals/ViewIncidentModal';
import EditIncidentModal from '../../components/Incidents/modals/EditIncidentModal';
import { debounce } from 'lodash';
import ClearIcon from '@mui/icons-material/Clear';
import FilterIncidents from '../../components/Incidents/FilterIncidents';
import SearchIcon from '@mui/icons-material/Search';
import ImportXml from './ImportXml';
import { useTableSearch } from '../../hooks/tables/useTableSearch';
import {
  deleteFilterParamsFromUrl,
  getFilterParams,
} from '../../components/Incidents/FilterIncidents/useFiltersUrlState';
import { useSearchParams } from 'react-router-dom';
import CreateNote from '../../components/Incidents/modals/CreateNote';

const Incidents = () => {
  const [_, setParams] = useSearchParams();
  const {
    searchQuery,
    paginationModel,
    setSearchQuery,
    setPaginationModel,
    localSearchState,
    setLocalSearchState,
  } = useTableSearch();

  const [viewIncident, setViewIncident] = useState(null);
  const [editIncident, setEditIncident] = useState(null);
  const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);
  
  let { incidentsList, loading, incidentsPagination } = useSelector(
    (state) => state.incidentsReducer
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDepartmentsList());
  }, [dispatch]);

  const requestForIncidents = () => {
    const params = getFilterParams();
    dispatch(
      getIncidentsList({
        page: paginationModel.page + 1,
        searchQuery: searchQuery,
        pageSize: paginationModel.pageSize,
        ...params,
      })
    );
  };
  
  useEffect(() => {
    requestForIncidents()
  }, [paginationModel.page, paginationModel.pageSize, searchQuery]);

  const debouncedHandlerSearch = useMemo(
    () =>
      debounce((query) => {
        setParams((prev) => {
          deleteFilterParamsFromUrl(prev);
          return prev;
        });

        setSearchQuery(query);
      }, 1000),
    [paginationModel.pageSize]
  );

  const handleSearchChange = (e) => {
    setLocalSearchState(e.target.value);
    debouncedHandlerSearch(e.target.value);
  };

  const clearSearch = () => {
    setLocalSearchState('');
    debouncedHandlerSearch('');
  };

  const openIncidentInfo = (id, edit = false) => {
    if (edit) {
      dispatch(getFormSettings(id));
      setEditIncident(true);
    } else {
      setViewIncident(true);
    }
    dispatch(getIncidentInfo(id));
  };

  const handlerSyncNeris = (id) => {
    dispatch(syncNeris(id)).then((resp) => {
      if (resp) {
        requestForIncidents();
      }
    });
  };

  const columns = useIncidentsColumns({
    openIncidentInfo,
    handlerSyncNeris,
    setOpenCreateNoteModal,
  });

  const rows = useMemo(
    () =>
      incidentsList.map((i) => ({
        ...i,
        id: i.incident_id,
        neris_status: i.neris_status ? i.neris_status : 'N/A',
      })),
    [incidentsList]
  );

  const onPaginationModelChange = (e) => {
    setPaginationModel(e);
  };

  return (
    <>
      <Box>
        <Card
          className="p-3 mt-4"
          sx={{
            boxShadow: '0px 4px 4px 0px #0000000F',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Incidents
            </Typography>
            <Box className="d-flex">
              {/*<Button*/}
              {/*  variant="outlined"*/}
              {/*  sx={{*/}
              {/*    background: '#F5F5F5',*/}
              {/*    mr: 2,*/}
              {/*    color: '#000',*/}
              {/*    border: '1px solid #F5F5F5',*/}
              {/*    fontSize: '16px',*/}
              {/*    '&:hover': {*/}
              {/*      borderColor: 'darkgray',*/}
              {/*    },*/}
              {/*  }}*/}
              {/*  endIcon={<ExpandMoreIcon sx={{ color: 'gray' }} />}*/}
              {/*>*/}
              {/*  Export*/}
              {/*</Button>*/}
              <ImportXml incidentsRequest={clearSearch} />
            </Box>
          </Box>

          <Grid
            container
            spacing={2}
            className="my-2"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid item xs={12} md={4}>
              <TextField
                color="secondary"
                label="Search incidents"
                placeholder="Search by incident number, type, or location..."
                size="small"
                value={localSearchState}
                onChange={handleSearchChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchQuery.length > 0 ? (
                        <ClearIcon
                          onClick={clearSearch}
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} className="d-flex justify-content-end">
              <FilterIncidents paginationModel={paginationModel} />
            </Grid>
            <Grid item xs={12}>
              {loading ? (
                <Box
                  sx={{ minHeight: '100px' }}
                  className="w-100 h-100 d-flex justify-content-center align-items-center"
                >
                  <CircularProgress />
                </Box>
              ) : !loading && incidentsList.length === 0 ? (
                <Typography className="mt-2" color="#6E7C87">
                  No incidents found matching your search.
                </Typography>
              ) : (
                <IncidentsTable
                  rows={rows}
                  columns={columns}
                  onPaginationModelChange={onPaginationModelChange}
                  paginationModel={paginationModel}
                  rowsCount={incidentsPagination.total}
                />
              )}
            </Grid>
          </Grid>
        </Card>
      </Box>
      <ViewIncidentModal
        viewIncident={viewIncident}
        setViewIncident={setViewIncident}
      />
      {!!editIncident && (
        <EditIncidentModal
          editIncident={editIncident}
          setEditIncident={setEditIncident}
        />
      )}

      <CreateNote open={openCreateNoteModal} setOpen={setOpenCreateNoteModal} />
    </>
  );
};

export default memo(Incidents);
